// --- React
import { useCallback, useMemo, useState } from "react";
// --- Config & Utils
import * as columnDefs from "../../../../../components/AGGrid/config/columns-def";
import { hasFeature } from "tap/utils/permissions.js";

/**
 * Custom hook for Offers grid utilities.
 *
 * Features:
 * - Manages sorting state and logic for the Offers grid
 * - Prepares column definitions with sorting controls
 * - Conditionally includes episode columns based on feature flag
 * - Client-side sorting (no API call)
 *
 * @param {object} params - Configuration object
 * @param {object} params.columnConfig - Object containing all column handlers and options
 * @returns {object} { columns, sortedColId, sortDirection, sortData }
 */
export function useOffersGridUtils({ columnConfig = {} } = {}) {
  // Default sort: ID descending
  const [sortedColId, setSortedColId] = useState("id");
  const [sortDirection, setSortDirection] = useState("desc");

  // Check feature flag for episode columns
  const hasEpisodeFeature = hasFeature(
    "TAP_OFFER_EPISODE_TIER_AND_EPISODE_EMA_PRICE_TYPE",
  );

  // Handle sorting: update local state only
  const setSort = useCallback((colId, direction) => {
    setSortedColId(colId);
    setSortDirection(direction);
  }, []);

  // Prepare column definitions with sorting controls and conditional episode columns
  const baseColumns = useMemo(() => {
    const colConfig = columnDefs["offerTable"](columnConfig);
    const defaultCols = colConfig["default"];

    if (hasEpisodeFeature) {
      // Get episode columns with the same handlers
      const episodeCols = colConfig.withEpisodeFeature(
        columnConfig.getEditingRows,
        columnConfig.onValueChange,
      );
      // Insert episode columns after seasonPassAmt (before pmxPlatform)
      const insertIndex = defaultCols.findIndex(
        (c) => c.field === "pmxPlatform",
      );
      if (insertIndex > -1) {
        return [
          ...defaultCols.slice(0, insertIndex),
          ...episodeCols,
          ...defaultCols.slice(insertIndex),
        ];
      }
      // Fallback: append at end if pmxPlatform not found
      return [...defaultCols, ...episodeCols];
    }

    return defaultCols;
  }, [columnConfig, hasEpisodeFeature]);

  const columns = useMemo(
    () =>
      baseColumns.map((col) => ({
        ...col,
        headerComponentParams: {
          ...col.headerComponentParams,
          setSort,
          sortedColId,
          sortDirection,
        },
      })),
    [setSort, sortedColId, sortDirection, baseColumns],
  );

  return { columns, sortedColId, sortDirection };
}
